/** ZidMicroAdapter — slim Zid adapter for v6 thin client. */
import type { MicroAdapter, PageContext, LiveCart } from './MicroAdapter';
import { EMPTY_CART } from './MicroAdapter';

function getZid(): any {
  if (typeof window === 'undefined') return null;
  const zid = (window as any).zid;
  return zid?.cart && zid?.products ? zid : null;
}

/** Strip locale prefix from Zid URLs: /ar-us/, /en/, /ar-sa/, etc. */
function stripLocale(path: string): string {
  return path.replace(/^\/(ar|en)(-[a-z]{2})?\//i, '/');
}

function extractProductId(): string | null {
  if (typeof window === 'undefined') return null;
  // 1. window.productObj (injected by Zid theme templates)
  const pObj = (window as any).productObj;
  if (pObj?.id) return String(pObj.id);
  // 2. DOM data attribute
  const el = typeof document !== 'undefined' ? document.querySelector('[data-product-id]') : null;
  if (el) return el.getAttribute('data-product-id');
  // 3. Hidden input
  const input = typeof document !== 'undefined' ? document.querySelector('#product-id') as HTMLInputElement | null : null;
  if (input?.value) return input.value;
  return null;
}

function extractCartData(response: any): any {
  // Format 1: Direct — response has products + products_count
  if (response?.products !== undefined && response?.products_count !== undefined) return response;
  // Format 2: { cart: { products } }
  if (response?.cart?.products) return response.cart;
  // Format 3: { data: { cart: { products } } }
  if (response?.data?.cart?.products) return response.data.cart;
  // Format 4: has products array
  if (Array.isArray(response?.products)) return response;
  return null;
}

function cartToLive(data: any): LiveCart {
  const items = data.products || [];
  const last = items.length > 0 ? items[items.length - 1] : null;
  return {
    total: data.total_value ?? data.products_subtotal ?? 0,
    itemCount: data.products_count ?? items.reduce((s: number, i: any) => s + (i.quantity || 1), 0),
    lastItemName: last?.name ?? null,
    currency: data.currency?.cart_currency?.code ?? 'SAR',
  };
}

export function createZidMicroAdapter(): MicroAdapter {
  const cleanups: (() => void)[] = [];

  return {
    detect() {
      return { platform: 'zid', available: !!getZid() };
    },

    getPageContext(): PageContext {
      const w = typeof window !== 'undefined' ? window : null;
      const url = w?.location?.href ?? '';
      const path = w?.location?.pathname ?? '';
      const stripped = stripLocale(path).toLowerCase();
      const base = { url, path };

      // Product page: /products/{slug}
      const prodMatch = stripped.match(/\/products?\/([^/]+)/);
      if (prodMatch && prodMatch[1]) {
        return { ...base, pageType: 'product', productId: extractProductId() ?? undefined };
      }
      // Products listing
      if (/\/products?\/?$/.test(stripped)) return { ...base, pageType: 'collection' };
      // Category page
      const catMatch = stripped.match(/\/categor(?:y|ies)\/([^/]+)/);
      if (catMatch) {
        const nameMatch = stripped.match(/\/categor(?:y|ies)\/[^/]+\/([^/]+)/);
        return { ...base, pageType: 'collection', collectionId: catMatch[1], collectionName: nameMatch?.[1] ? decodeURIComponent(nameMatch[1]) : undefined };
      }
      if (/\/cart\/?$/.test(stripped)) return { ...base, pageType: 'cart' };
      if (stripped.includes('/checkout')) return { ...base, pageType: 'checkout' };
      // Order confirmation: /order-completed/{code}/invoice?id={orderId}
      const orderMatch = stripped.match(/\/order-completed\/([^/]+)/);
      if (orderMatch || stripped.includes('/order-confirmation')) {
        const params = w ? new URLSearchParams(w.location.search) : null;
        return { ...base, pageType: 'order_confirmation', orderCode: orderMatch?.[1] || params?.get('id') || params?.get('order_id') || undefined };
      }
      if (stripped.includes('/search')) {
        const q = w ? new URLSearchParams(w.location.search).get('q') ?? undefined : undefined;
        return { ...base, pageType: 'search', searchQuery: q };
      }
      if (stripped === '' || stripped === '/') return { ...base, pageType: 'home' };
      return { ...base, pageType: 'other' };
    },

    async getCart(): Promise<LiveCart> {
      try {
        const zid = getZid();
        if (!zid?.cart?.get) return EMPTY_CART;
        const res = await zid.cart.get();
        const data = extractCartData(res);
        return data ? cartToLive(data) : EMPTY_CART;
      } catch { return EMPTY_CART; }
    },

    onCartChange(handler) {
      if (typeof document === 'undefined') return;
      const cb = async () => { try { handler(await this.getCart()); } catch {} };
      document.addEventListener('cartUpdate', cb);
      document.addEventListener('productAdded', cb);
      cleanups.push(() => {
        document.removeEventListener('cartUpdate', cb);
        document.removeEventListener('productAdded', cb);
      });
    },

    onCheckoutIntent(handler) {
      if (typeof document === 'undefined') return;
      const listener = (e: Event) => {
        let el = e.target as HTMLElement | null;
        for (let i = 0; el && i < 5; i++, el = el.parentElement) {
          const text = (el.textContent ?? '').toLowerCase();
          const href = (el.getAttribute('href') ?? '').toLowerCase();
          if (text.includes('checkout') || text.includes('\u0627\u0644\u062F\u0641\u0639') || text.includes('\u0625\u062A\u0645\u0627\u0645') || text.includes('\u0627\u062A\u0645\u0627\u0645') || href.includes('checkout')) {
            handler(); return;
          }
        }
      };
      document.addEventListener('click', listener, true);
      cleanups.push(() => document.removeEventListener('click', listener, true));
    },

    onUserLogin(handler) {
      let stopped = false;
      let attempts = 0;
      const MAX_ATTEMPTS = 3; // Stop polling after 3 failed attempts (6 seconds)
      const poll = setInterval(async () => {
        if (stopped) return;
        try {
          const zid = getZid();
          const user = zid?.account?.get ? await zid.account.get() : null;
          const id = user?.id ?? user?.data?.id;
          if (id) {
            stopped = true;
            clearInterval(poll);
            handler({ id: String(id), email: user?.email ?? user?.data?.email });
          } else {
            // No user data — guest visitor, stop polling after max attempts
            if (++attempts >= MAX_ATTEMPTS) {
              stopped = true;
              clearInterval(poll);
            }
          }
        } catch {
          // Auth error (401) — guest visitor, stop polling after max attempts
          if (++attempts >= MAX_ATTEMPTS) {
            stopped = true;
            clearInterval(poll);
          }
        }
      }, 2000);
      cleanups.push(() => { stopped = true; clearInterval(poll); });
    },

    async addToCart(productId, quantity = 1) {
      try {
        const zid = getZid();
        if (!zid?.cart?.addProduct) return false;
        await zid.cart.addProduct({ product_id: productId, quantity });
        return true;
      } catch { return false; }
    },

    async applyCoupon(code) {
      try {
        const zid = getZid();
        if (!zid?.cart?.applyCoupon) return false;
        await zid.cart.applyCoupon({ coupon_code: code });
        return true;
      } catch { return false; }
    },

    destroy() {
      cleanups.forEach((fn) => fn());
      cleanups.length = 0;
    },
  };
}
